'use client'

import { type ComponentProps, createContext, type FC, useContext } from 'react'
import { type AppConfig } from 'ecosystem'

const fallbackConfigFieldKey = Symbol('fallbackConfigFieldKey')
const isFallbackAppliedKey = Symbol('isFallbackAppliedKey')

export const createAppConfigContext = (
  appConfig: AppConfig & { [fallbackConfigFieldKey]?: AppConfig; [isFallbackAppliedKey]?: boolean }
) => {
  const AppConfigContext = createContext<AppConfig | undefined>(undefined)

  type ProviderProps = ComponentProps<typeof AppConfigContext.Provider>

  const Provider: FC<ProviderProps> = ({ value, children }) => {
    // save original prebuild config
    if (!appConfig[fallbackConfigFieldKey]) {
      appConfig[fallbackConfigFieldKey] = structuredClone(appConfig)
    }

    if (value) {
      appConfig[isFallbackAppliedKey] = false

      Object.keys(appConfig).forEach((key) => {
        if (key === 'labels') {
          appConfig[key] = {
            ...appConfig[key],
            ...value[key]
          }
        } else {
          // @ts-expect-error -- key as string
          appConfig[key] = value[key]
        }
      })

      appConfig.theme = value.theme
      // recover original prebuild config from fallback
    } else if (!appConfig[isFallbackAppliedKey]) {
      Object.keys(appConfig).forEach((key) => {
        // @ts-expect-error -- key as string
        appConfig[key] = appConfig[fallbackConfigFieldKey][key]
      })

      appConfig[isFallbackAppliedKey] = true
    }

    return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>
  }

  const useAppConfigContext = () => {
    const contextValue = useContext(AppConfigContext)

    return contextValue ?? appConfig
  }

  return [Provider, useAppConfigContext, AppConfigContext] as const
}
